import { useCallback, useState } from 'react'
import axios from 'axios'
import config from 'config'
import { useTranslation } from 'react-i18next'

import {
  SESSIONS_STEP_CONFIRMATION,
  SESSIONS_STEP_PLANNING,
  SESSIONS_STEP_SOLD,
} from 'components/organisms/Sessions/sessionsLib'

import useSnackbar from '../components/organisms/Snackbar/useSnackbar'
const COMMON_ERROR_MESSAGE = 'common.error.fetchData'

const useSession = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    session: {},
    error: null,
  })

  const parseError = useCallback(
    (e, defaultMessage) => {
      // let message = defaultMessage || e.message
      let message = defaultMessage || e.response?.data?.message

      if (Number(e?.response?.status) === 400) {
        if (e.response?.data?.message?.startsWith('Duplicated plates:')) {
          message = t('common.error.sessions.fileUpload.duplicatedPlates', {
            attribute: e.response.data.message.split(':')[1],
          })
        }
        if (
          e.response?.data?.message?.startsWith(
            'Session is not in planning status'
          )
        ) {
          message = t('common.error.sessions.notInPlanning')
        }
        if (
          e.response?.data?.message?.endsWith('the session is not confirmed')
        ) {
          message = t('common.error.sessions.notConfirmed')
        }
        if (
          e.response?.data?.message?.endsWith(
            'type should be equal to one of the allowed values'
          )
        ) {
          message = t('common.error.sessions.fileUpload.wrongSoldTypeValue')
        }
      }

      showSnackbarMessage({
        severity: 'error',
        message,
      })
    },
    [showSnackbarMessage, t]
  )

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error,
      }))
    },
    [setState]
  )

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const setSuccessFetching = useCallback(
    (session) => {
      setState((s) => ({
        ...s,
        session,
        isFetching: false,
        isUpdating: false,
        error: null,
      }))
    },
    [setState]
  )

  const fetchSession = useCallback(
    async (sessionId) => {
      setStartFetching()

      try {
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}`
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        let message = t(COMMON_ERROR_MESSAGE, {
          attribute: e.response?.status,
        })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const fetchCurrentSession = useCallback(async () => {
    await fetchSession('current')
  }, [fetchSession])

  const fetchCurrentAssignmentSession = useCallback(async () => {
    await fetchSession('current-assignment')
  }, [fetchSession])

  const fetchCurrentCarsBookedSession = useCallback(async () => {
    await fetchSession('current-cars-booked')
  }, [fetchSession])

  const updateDate = useCallback(
    async (sessionId, sessionAgendaId, type, date) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/agenda/${sessionAgendaId}`,
          { type, date }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updateAllDates = useCallback(
    async (sessionId, data) => {
      setStartUpdating()

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/agenda`,
          { ...data }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updatePlatePricing = useCallback(
    async (sessionId, plate, prices) => {
      setStartUpdating()

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/pricing/${plate}`,
          { prices }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updateCars = useCallback(
    async (sessionId, cars, fileName, fetchAdditional = false) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/cars`,
          { cars, fileName, fetchAdditional }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const ingestCars = useCallback(
    async (sessionId) => {
      setStartUpdating()

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/ingest-cars`,
          {}
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const ingestPrices = useCallback(
    async (sessionId) => {
      setStartUpdating()

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/ingest-prices`,
          {}
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const ingestSold = useCallback(
    async (sessionId) => {
      setStartUpdating()

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/ingest-sold`,
          {}
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updateShowOtherPartners = useCallback(
    async (sessionId, value) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/show-other-partners`,
          { value }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updateCanReprice = useCallback(
    async (sessionId, value) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/can-reprice`,
          { value }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updatePrices = useCallback(
    async (sessionId, cars, fileName) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/pricing`,
          { cars, fileName }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updateSold = useCallback(
    async (sessionId, cars, fileName) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/sold`,
          { cars, fileName }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updateCap = useCallback(
    async (sessionId, partnerId, type, value) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/caps/${partnerId}`,
          { type, value }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.sessions.updateCaps'),
        })
      }
    },
    [
      setStartUpdating,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const updateBlindCap = useCallback(
    async (sessionId, partnerId, type, value) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/blind-caps/${partnerId}`,
          { type, value }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.sessions.updateCaps'),
        })
      }
    },
    [
      setStartUpdating,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const updateAssignmentCap = useCallback(
    async (session, partnerId, cap, callback) => {
      setStartUpdating()
      try {
        const result = await axios.post(
          `${config.apiBaseUrl}sessions/${session?.sessionId}/partner/${partnerId}/update-assignment-cap`,
          { cap }
        )
        if (result.data.body?.success === 'ok' && session.assignmentCaps) {
          const partner =
            session.assignmentCaps &&
            session.assignmentCaps.find(
              (partner) => partner.partnerId === partnerId
            )

          if (partner) partner.cap = Number(cap)

          setSuccessFetching({ ...session })
        } else {
          setStopUpdating()
        }

        callback && callback()
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: e.response?.data?.message,
        })
      }
    },
    [setStartUpdating, setStopUpdating, setSuccessFetching, showSnackbarMessage]
  )

  const confirmStep = useCallback(
    async (sessionId, step, callback) => {
      setStartUpdating()
      const postStep =
        step === SESSIONS_STEP_PLANNING
          ? 'date'
          : step === SESSIONS_STEP_CONFIRMATION
          ? 'session'
          : step

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/${postStep}/confirm`,
          {}
        )

        setSuccessFetching({ ...state.session, ...results.data.body })
        callback && callback()
        // setStopUpdating()
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: e.response?.data?.message,
        })
      }
    },
    [
      setStartUpdating,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      state.session,
    ]
  )

  const confirmBlindCaps = useCallback(
    async (sessionId, callback) => {
      setStartUpdating()

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/blind-caps/confirm`,
          {}
        )

        setSuccessFetching({ ...state.session, ...results.data.body })
        callback && callback()
        return true
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: e.response?.data?.message,
        })
        return false
      }
    },
    [
      setStartUpdating,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      state.session,
    ]
  )

  const confirmNoSoldCars = useCallback(
    async (sessionId) => {
      setStartUpdating()

      const cars = []
      const fileName = null

      try {
        const resultUpdate = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/sold`,
          { cars, fileName }
        )
        const resultConfirm = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/${SESSIONS_STEP_SOLD}/confirm`,
          {}
        )

        setSuccessFetching({
          ...resultUpdate.data.body,
          ...resultConfirm.data.body,
        })
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: e.response?.data?.message,
        })
      }
    },
    [setStartUpdating, setStopUpdating, setSuccessFetching, showSnackbarMessage]
  )

  const checkIfAllFilesAreUploaded = useCallback(async () => {
    setStartFetching()

    try {
      const result = await axios.get(
        `${config.apiBaseUrl}sessions/current/all-files-uploaded`
      )

      setSuccessFetching({ session: { ...state.session, ...result.data.body } })
    } catch (e) {
      setStopFetching(e)

      showSnackbarMessage({
        severity: 'error',
        message: t(COMMON_ERROR_MESSAGE, {
          attribute: e.response?.status,
        }),
      })
    }
  }, [
    state.session,
    setStartFetching,
    setStopFetching,
    setSuccessFetching,
    showSnackbarMessage,
    t,
  ])

  const syncAdditionalInfo = useCallback(async () => {
    setStartUpdating()

    try {
      const result = await axios.get(
        `${config.apiBaseUrl}sessions/${state.session.sessionId}/sync-additional-info`
      )

      setSuccessFetching({ ...state.session, ...result.data.body })
    } catch (e) {
      setStopUpdating()

      showSnackbarMessage({
        severity: 'error',
        message: t(COMMON_ERROR_MESSAGE, {
          attribute: e.response?.status,
        }),
      })
    }
  }, [
    state.session,
    setStartUpdating,
    setStopUpdating,
    setSuccessFetching,
    showSnackbarMessage,
    t,
  ])

  return {
    session: state.session,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchSession,
    fetchCurrentSession,
    fetchCurrentAssignmentSession,
    fetchCurrentCarsBookedSession,
    ingestCars,
    ingestPrices,
    ingestSold,
    updateDate,
    updateAllDates,
    updateCars,
    updatePrices,
    updateSold,
    updateCap,
    updateBlindCap,
    updateAssignmentCap,
    updatePlatePricing,
    updateShowOtherPartners,
    updateCanReprice,
    confirmStep,
    confirmBlindCaps,
    confirmNoSoldCars,
    checkIfAllFilesAreUploaded,
    syncAdditionalInfo,
  }
}

export default useSession
